import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { ConstantsService } from './constants.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationBetaNewService } from './authentication-beta-new.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;
  private apiurl: string;
  constructor(
    private http: HttpClient,
    private _constant: ConstantsService,
    private router: Router,
    private route: ActivatedRoute,
    private authenticationServiceNew: AuthenticationBetaNewService
  ) {
    this.currentUserSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem('currentUser') || '{}')
    );
    this.currentUser = this.currentUserSubject.asObservable();
    this.apiurl = this._constant.ApiUrl;
  }

  public get currentUserValue() {
    return this.currentUserSubject.value;
  }

  login(postdata: {}): Observable<any> {
    return this.http
      .post<any>(`${this.apiurl}/api/v2/user/auth-login`, postdata)
      .pipe(
        map((user) => {
          // Check if user is active/inactive or error response
          if (!user || user.status === 'error' || user.status === 'inactive' || user.status === 'suspended' || user.status === 'failed' || user.status === 0 || user.is_active === false || user.is_active === 0 || user.is_active === '0' || user.isActive === false || user.error) {
            const errMsg = user?.message || user?.error || 'Access denied: Account is suspended/inactive';
            throw new Error(errMsg);
          }

          // ✅ Save full user data
          localStorage.setItem('currentUser', JSON.stringify(user));

          // ✅ Extract and save privilege data
          this.extractAndSavePrivilege(user);

          // ✅ Notify other parts of the app
          localStorage.setItem('auth-event', 'login');
          
          // Update both OLD and NEW service subjects
          this.currentUserSubject.next(user);
          this.sendCurrentUserSubject(user);  // ✅ This updates NEW service
          this.sendLoginUser(true);
          this.startRefreshTokenTimer();

          return user;
        })
      );
  }

  user_login(postdata: {}): Observable<any> {
    return this.http
      .post<any>(`${this.apiurl}/api/v2/user/auth-login`, postdata)
      .pipe(
        map((user) => {
          // Check if user is active/inactive or error response
          if (!user || user.status === 'error' || user.status === 'inactive' || user.status === 'suspended' || user.status === 'failed' || user.status === 0 || user.is_active === false || user.is_active === 0 || user.is_active === '0' || user.isActive === false || user.error) {
            const errMsg = user?.message || user?.error || 'Access denied: Account is suspended/inactive';
            throw new Error(errMsg);
          }

          // ✅ Save full user data
          localStorage.setItem('currentUser', JSON.stringify(user));

          // ✅ Extract and save privilege data
          this.extractAndSavePrivilege(user);

          localStorage.setItem('auth-event', 'login');
          
          // Update both OLD and NEW service subjects
          this.currentUserSubject.next(user);
          this.sendCurrentUserSubject(user);  // ✅ This updates NEW service
          this.sendLoginUser(true);
          this.startRefreshTokenTimer();

          return user;
        })
      );
  }

  sendCurrentUserSubject(data: any) {
    this.authenticationServiceNew.currentUserSubject.next(data);
  }

  sendLoginUser(data: any) {
    this.authenticationServiceNew.loginUser.next(data);
  }

  auto_login(accesskey: any): Observable<any> {
    return this.http.post<any>(`${this.apiurl}/api/v2/user/auto-login`, { accesskey }).pipe(
      map((user) => {
        // Check if user is active/inactive or error response
        if (!user || user.status === 'error' || user.status === 'inactive' || user.status === 'suspended' || user.status === 'failed' || user.status === 0 || user.is_active === false || user.is_active === 0 || user.is_active === '0' || user.isActive === false || user.error) {
          const errMsg = user?.message || user?.error || 'Access denied: Account is suspended/inactive';
          throw new Error(errMsg);
        }
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.extractAndSavePrivilege(user);
        this.currentUserSubject.next(user);
        this.sendCurrentUserSubject(user);
        this.sendLoginUser(true);
        this.startRefreshTokenTimer();
        return user;
      })
    );
  }

  private extractAndSavePrivilege(user: any): any {
    if (!user) return null;
    const userId = user.userid || user.userId || user.id;
    let userPrivilege: any = null;

    if (user.user_privilege) {
      if (userId && user.user_privilege[`user_${userId}`]) {
        userPrivilege = user.user_privilege[`user_${userId}`]['ipd-mongo-db']
          || user.user_privilege[`user_${userId}`]['ipd']
          || user.user_privilege[`user_${userId}`]['IPD']
          || user.user_privilege[`user_${userId}`];
      }
      if (!userPrivilege) {
        userPrivilege = user.user_privilege['ipd-mongo-db']
          || user.user_privilege['ipd']
          || user.user_privilege['IPD']
          || (typeof user.user_privilege === 'object' && !user.user_privilege[`user_${userId}`] ? user.user_privilege : null);
      }
    }

    if (userPrivilege) {
      userPrivilege.View = userPrivilege.View === true || userPrivilege.View === 'true' || userPrivilege.View === 1 || userPrivilege.View === '1' || userPrivilege.View === 'yes';
      userPrivilege.Download = userPrivilege.Download === true || userPrivilege.Download === 'true' || userPrivilege.Download === 1 || userPrivilege.Download === '1' || userPrivilege.Download === 'yes';

      userPrivilege.Search = Number(userPrivilege.Search ?? 0);
      userPrivilege.DownloadCount = Number(userPrivilege.DownloadCount ?? userPrivilege.InitialDownloadCount ?? 0);
      userPrivilege.DailySearchLimit = Number(userPrivilege.DailySearchLimit ?? 0);
      userPrivilege.DailyDownloadLimit = Number(userPrivilege.DailyDownloadLimit ?? 0);

      const existingPrivStr = localStorage.getItem('user_privilege') || localStorage.getItem('userPrivilege');
      if (existingPrivStr) {
        try {
          const parsed = JSON.parse(existingPrivStr);
          userPrivilege.today_search_count = Number(parsed.today_search_count ?? 0);
          userPrivilege.today_download_count = Number(parsed.today_download_count ?? 0);
        } catch (e) {
          userPrivilege.today_search_count = 0;
          userPrivilege.today_download_count = 0;
        }
      } else {
        userPrivilege.today_search_count = 0;
        userPrivilege.today_download_count = 0;
      }

      localStorage.setItem('user_privilege', JSON.stringify(userPrivilege));
      localStorage.setItem('userPrivilege', JSON.stringify(userPrivilege));

      this.authenticationServiceNew.userPrivilegeSubject.next(userPrivilege);
      return userPrivilege;
    }
    return null;
  }

  logout() {
    // remove user from local storage and set current user to null
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
    this._constant.isLoggedIn = false;
    let currentaccesskey = this.route.snapshot.queryParams['access_key'];
    let newurlroute = this.router.url.replace(
      `?access_key=${currentaccesskey}`,
      ''
    );
    console.log(newurlroute);
    //this.router.navigateByUrl(newurlroute);
    //window.location.reload();
  }

  refreshToken() {
    let currentuser = localStorage.getItem('currentUser');
    let var_userid = 0;
    let email = '';
    if (currentuser) {
      let userdata = JSON.parse(currentuser);
      var_userid = userdata.userid;
      email = userdata.email;
    }
    return this.http
      .post<any>(`${this.apiurl}/api/v2/user/refresh-token`, {
        userid: var_userid,
        email: email,
      })
      .pipe(
        map((user) => {
          this.currentUserSubject.next(user);
          localStorage.setItem('currentUser', JSON.stringify(user));
          this.startRefreshTokenTimer();
          return user;
        })
      );
  }

  // getUserPrivilege(postdata: any) {
  //   return this.http.post(this.apiurl + '/usermaster_manage', postdata);
  // }

  // helper methods

  private refreshTokenTimeout: any;

  private startRefreshTokenTimer() {
    // parse json object from base64 encoded jwt token
    const jwtToken = JSON.parse(
      atob(this.currentUserValue.token.split('.')[1])
    );

    // set a timeout to refresh the token a minute before it expires
    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;
    this.refreshTokenTimeout = setTimeout(
      () => this.refreshToken().subscribe(),
      timeout
    );
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
}
